/**
 * The main loop's drawing pass over the software sprites (D_E90E), which is where the C64 bursts
 * bubbles (spec 02 §4 Chain, Pop points, §7.1, §7.2): each slot's type picks a routine through the
 * vectors at $040C. A bursting bubble ($34) bursts every bubble within 24 px (routine_3D2D) - one
 * lower in slot order bursts in the same pass, one higher on the next - then releases what it holds
 * (routine_3D77, routine_3DB0); the burst frames and the vanish count down; a trapped monster's sprite
 * is put on its bubble (D_3CB2). At the end a chain that grew no further this pass is paid (L_E97F).
 */

import { addScore } from '../score';
import { startBolt } from './bolts';
import {
  BURST_1, BURST_2, BURST_3, FIRE, GONE, GONE2, JUST_BLOWN, LETTER, LIGHTNING, POPPING, QUIET_POP, SMALL, WATER,
  isBubbleCode, isLetterCode, isSpecialCode, isTrappedCode,
} from './codes';
import { collectLetter } from './extend';
import { startDrop } from './fire';
import { killMonster } from './items';
import { bySlot, freeBubble, setCode } from './slots';
import { systemOf } from './system';
import { CHAIN_BONUS_UNITS, CHAIN_RANGE, POP_UNITS, SPECIAL_POP_UNITS } from './tuning';
import { startWater } from './water';
import type { Bubble, World } from '../types';

/** D_E90E: one pass over the slots, slot 17 first, then the chain bonus. */
export function burstPass(world: World): void {
  const sys = systemOf(world);
  sys.chainSeen = [sys.chain[0], sys.chain[1]];
  for (const b of bySlot(world)) {
    const m = b.motion;
    switch (m.code) {
      case JUST_BLOWN: setCode(b, SMALL); break;
      case POPPING: spreadChain(world, b); release(world, b, true); break;
      case QUIET_POP: release(world, b, false); break;
      case BURST_1: nextBurst(b, BURST_2, 1); break;
      case BURST_2: nextBurst(b, BURST_3, 2); break;
      case BURST_3: nextBurst(b, GONE, 3); break;
      case GONE: nextBurst(b, GONE2, m.burst >= 0 ? 4 : -1); break;
      case GONE2: freeBubble(world, b); break;
      default: if (isTrappedCode(m.code)) holdMonster(world, b);
    }
  }
  payChains(world);
}

function nextBurst(b: Bubble, code: number, frame: number): void {
  b.motion.burst = frame;
  setCode(b, code);
}

/**
 * D_3CB2: the trapped monster's sprite takes the bubble's position; its angry colour is cleared
 * ($3CC1: D_872A = 0) - it turns angry again when it escapes.
 */
function holdMonster(world: World, b: Bubble): void {
  if (b.content.kind !== 'monster') return;
  const id = b.content.monsterId;
  const mon = world.monsters.find((x) => x.id === id);
  if (!mon) return;
  mon.x = b.x;
  mon.y = b.y;
  mon.angry = false;
}

/**
 * routine_3D2D: every other bubble (type < $24, shots too) within |dx| < 24 and |dy| < 24 bursts,
 * credited to the same player - unless this one is still a shot ($A9B2,x set: a shot burst by a chain
 * passes nothing on).
 */
function spreadChain(world: World, b: Bubble): void {
  if (b.motion.steps !== 0) return;
  for (const o of world.bubbles) {
    if (o === b || !isBubbleCode(o.motion.code)) continue;
    if (Math.abs(b.x - o.x) >= CHAIN_RANGE || Math.abs(b.y - o.y) >= CHAIN_RANGE) continue;
    o.motion.saved = o.motion.code;
    o.motion.credit = b.motion.credit;
    setCode(o, POPPING);
  }
}

/**
 * routine_3D77 / routine_3DB0: what a bursting bubble lets go. A trapped monster dies, its item by its
 * place in the player's chain; the bubble itself scores nothing ($3DA9 skips the pop points). Anything
 * else, if burst by a player ($34, not a quiet $3A): 10 points (100 for water, fire, lightning) and a
 * letter's slot. Water starts the stream (one at a time); the rest burst on screen.
 */
function release(world: World, b: Bubble, popped: boolean): void {
  const m = b.motion;
  const saved = m.saved;
  const credit = popped ? m.credit : -1;
  if (popped || isBubbleCode(saved)) {
    world.events.push({ type: 'pop', bubbleId: b.id, player: credit, content: b.content.kind });
  }
  if (isTrappedCode(saved)) {
    killTrapped(world, b);
    setCode(b, GONE2);
    return;
  }
  if (credit !== -1) {
    addScore(world, credit, isSpecialCode(saved) ? SPECIAL_POP_UNITS : POP_UNITS);
    if (isLetterCode(saved)) collectLetter(world, credit, (saved - LETTER) >> 1);
  }
  // $3E06: water starts the stream if none is running (else it bursts). $3E2C: lightning becomes a bolt,
  // $3E5E: fire a falling drop, each in the bubble's slot - also when burst quietly, by the clear's sweep.
  if (saved === WATER && startWater(world, b)) {
    setCode(b, GONE2);
    return;
  }
  if (saved === LIGHTNING) return startBolt(world, b, credit);
  if (saved === FIRE) return startDrop(world, b, credit);
  m.burst = 0;
  setCode(b, BURST_1);
}

/** routine_3D77: the monster dies; the player's chain count ($46,x) goes up and picks its item ($8892). */
function killTrapped(world: World, b: Bubble): void {
  if (b.content.kind !== 'monster') return;
  const id = b.content.monsterId;
  b.content = { kind: 'empty' };
  const mon = world.monsters.find((x) => x.id === id);
  if (!mon) return;
  const credit = b.motion.credit;
  let place = 1;
  if (credit !== -1) {
    const sys = systemOf(world);
    place = ++sys.chain[credit];
  }
  killMonster(world, mon, place, 'pop');
}

/**
 * L_E97F: a player's chain that did not grow during this pass has ended: pay table $AB52 by the
 * number of monsters in it - once, for the whole chain - and start counting again.
 */
function payChains(world: World): void {
  const sys = systemOf(world);
  for (const id of [1, 0] as const) {
    const kills = sys.chain[id];
    if (kills === 0 || kills !== sys.chainSeen[id]) continue;
    sys.chain[id] = 0;
    const bonus = CHAIN_BONUS_UNITS[Math.min(kills, CHAIN_BONUS_UNITS.length - 1)] ?? 0;
    addScore(world, id, bonus);
    world.events.push({ type: 'chain', player: id, kills, bonus });
  }
}
